import { ReactElement } from 'react'
import { NavLink } from 'react-router-dom'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { Formik, FormikErrors, FormikTouched, getIn } from 'formik'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import EmptyState from 'components/v2/EmptyState'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import { InputCurrency } from 'components/InputCurrency'
import { Label } from 'components/v2/Label'
import { Row } from '@10xLabs/react-web/components/Row'
import { SelectTags } from 'components/v2/SelectTags'
import { Table, Thead, Th, Tbody, Tr, Td } from '@10xLabs/react-web/components/Table'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { CompanyLineAutocomplete } from 'features/Lines/components/CompanyLineAutocomplete'
import FormLayout from 'features/App/components/FormLayout'
import { confirmationModalitiesMessages } from 'features/Services/components/ConfirmationModalities/messages'
import { getUnprocessableErrors } from 'utils/error'
import { sanitizePrice } from 'utils/math'
import { schema } from './schema'
import { INewAddonServiceFormProps, INewAddonServiceProps } from './types'
import { ICompany } from 'features/Companies/types'
import { ICompanyLine } from 'features/Lines/types'
import { EConfirmationModality } from 'features/Bookings/types'
import { IFeeForm, ITicketFeeForm } from 'features/Ancillaries/types'
import { safeFormatMessage } from 'utils/intl'
import { GTSegmentSuggestion } from 'gql/types.generated'
import { SelectSalesChannelsV2 } from 'features/Services/components/SelectSalesChannelsV2'
import { ICurrency, ISalesChannel } from 'features/Services/components/NewServicesV2/components/OfferSettingsCard/types'
import { canSelectCompany } from 'features/Login/utils'
import { useLogin } from 'features/Login/hooks'
import { MultiSegmentAutocomplete } from 'features/Segments/components/MultiSegmentAutocomplete'

const StyledTicketQuantity = styled.span<TSFixMe>`
  font-size: 16px;
  font-weight: 500;
  padding: 9px 0px;
`

const StyledTh = styled(Th)<TSFixMe>`
  font-weight: normal;
`

const StyledCell = styled(Td)<TSFixMe>`
  padding: 15px 10px;
`

const StyledTicketCell = styled(StyledCell)<TSFixMe>`
  align-items: center;
  display: flex;
`

const CONFIRMATION_MODALITIES_LIST = [EConfirmationModality.PAID, EConfirmationModality.RESERVED]

const messages = defineMessages({
  companyLabel: {
    id: 'addonService.form.company.label',
    defaultMessage: 'Company'
  },
  companyPlaceholder: {
    id: 'addonService.form.company.placeholder',
    defaultMessage: 'Select company'
  },
  companyLineLabel: {
    id: 'addonService.form.companyLine.label',
    defaultMessage: 'Company Line'
  },
  companyLinePlaceholder: {
    id: 'addonService.form.companyLine.label',
    defaultMessage: 'Select a company line'
  },
  salesChannelsLabel: {
    id: 'addonService.form.salesChannels.label',
    defaultMessage: 'Sales Channel'
  },
  salesChannelsPlaceholder: {
    id: 'addonService.form.salesChannels.placeholder',
    defaultMessage: 'Select a sales channel'
  },
  modalitiesLabel: {
    id: 'addonService.form.modalities.label',
    defaultMessage: 'Confirmation modalities'
  },
  modalitiesPlaceholder: {
    id: 'addonService.form.modalities.placeholder',
    defaultMessage: 'Select confirmation modalities'
  },
  segmentsLabel: {
    id: 'addonService.form.segments.placeholder',
    defaultMessage: 'Segments'
  },
  segmentsPlaceholder: {
    id: 'addonService.form.segments.label',
    defaultMessage: 'Select segments'
  }
})

const getCurrenciesBySalesChannels = (salesChannels: ISalesChannel[]): ICurrency[] => {
  return salesChannels.reduce((result: TSFixMe[], salesChannel) => {
    const currency = salesChannel.country?.currency
    if (!currency) return result
    if (result.some(({ code }) => code === currency.code)) return result
    return [
      ...result,
      {
        id: currency.id ?? '',
        code: currency.code ?? ''
      }
    ]
  }, [])
}

const NewAddonServiceForm = ({
  values,
  touched,
  errors,
  setFieldValue,
  setValues,
  handleSubmit,
  error,
  loading,
  isEdit,
  addonService
}: INewAddonServiceFormProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const getExistingFee = (quantity: number, currency: string): IFeeForm | undefined => {
    const ticketFeesByQuantity = addonService?.ticketFees?.find((ticketFee): TSFixMe => ticketFee.quantity === quantity)
    return ticketFeesByQuantity?.fees.find((fee): TSFixMe => fee.currencyCode === currency)
  }

  const getFilteredConfirmationModalities = (values: EConfirmationModality[]): EConfirmationModality[] => {
    return CONFIRMATION_MODALITIES_LIST.filter((modality): TSFixMe => values.indexOf(modality) === -1)
  }

  const handleSalesChannelsChange = (salesChannels: ISalesChannel[]): void => {
    const currencies = getCurrenciesBySalesChannels(salesChannels)

    const newTicketFees = values.ticketFees.map(({ quantity, fees }): TSFixMe => {
      const newFees = currencies.map((currency) => {
        const includesCurrency = fees.some((fee) => fee.currencyCode === currency.code)

        if (includesCurrency) {
          return fees.find((fee): TSFixMe => fee.currencyCode === currency.code)
        } else {
          const existingFee = getExistingFee(quantity, currency.code)
          return existingFee ? existingFee : { amount: '', currencyCode: currency.code }
        }
      })

      return {
        quantity,
        fees: newFees
      }
    })

    setFieldValue('salesChannels', salesChannels)
    setFieldValue('ticketFees', newTicketFees)
  }

  const handleFeeChange = (ticketPosition: number, currencyPosition: number, value: string): void => {
    setFieldValue(`ticketFees[${ticketPosition}].fees[${currencyPosition}].amount`, value)
  }

  const isFormikValidation = <T,>(item?: T | string | string[]): item is T => {
    return typeof item === 'object'
  }

  const hasFeeTouched = (
    ticketPosition: number,
    currencyPosition: number,
    ticketFees?: FormikTouched<ITicketFeeForm>[] | string | string[]
  ): boolean | undefined => {
    if (ticketFees && isFormikValidation<FormikTouched<ITicketFeeForm>[]>(ticketFees)) {
      const fees = ticketFees[ticketPosition] && ticketFees[ticketPosition].fees
      if (fees && isFormikValidation<FormikTouched<IFeeForm>[]>(fees)) {
        return fees[currencyPosition] ? fees[currencyPosition].amount : undefined
      }
    }

    return undefined
  }

  const hasFeeErrors = (
    ticketPosition: number,
    currencyPosition: number,
    ticketFees?: FormikErrors<ITicketFeeForm>[] | string | string[]
  ): string | undefined => {
    if (ticketFees && isFormikValidation<FormikErrors<ITicketFeeForm>[]>(ticketFees)) {
      const fees = ticketFees[ticketPosition] && ticketFees[ticketPosition].fees
      if (fees && isFormikValidation<FormikErrors<IFeeForm>[]>(fees)) {
        return fees[currencyPosition] && fees[currencyPosition].amount
      }
    }

    return undefined
  }

  const currencies = getCurrenciesBySalesChannels(values.salesChannels)

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="addonService.form.general.label" defaultMessage="General" />
            </Heading.H5>
          </Column>
        </Row>
        {canSelectCompany(login.accessToken) && (
          <Row>
            <Column width="50%">
              <CompanyAutocomplete
                id="company"
                label={intl.formatMessage(messages.companyLabel)}
                placeholder={intl.formatMessage(messages.companyPlaceholder)}
                value={values.company.name}
                onChange={(value: ICompany): void => {
                  setValues({
                    ...values,
                    company: value,
                    companyLine: { id: '', name: '' }
                  })
                }}
                touched={touched.company && touched.company.id}
                errors={errors.company && errors.company.id}
                disabled={isEdit}
              />
            </Column>
          </Row>
        )}
        <Row>
          <Column width="50%">
            <CompanyLineAutocomplete
              id="companyLine"
              label={intl.formatMessage(messages.companyLineLabel)}
              placeholder={intl.formatMessage(messages.companyLinePlaceholder)}
              value={values.companyLine.name}
              companyId={values.company.id}
              onChange={(value: ICompanyLine): void => {
                setFieldValue('companyLine', value)
              }}
              touched={touched.companyLine && touched.companyLine.id}
              errors={errors.companyLine && errors.companyLine.id}
              disabled={isEdit}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="addonService.form.salesChannels.label" defaultMessage="Sales Channel" />
            </Label>
            <SelectSalesChannelsV2
              id="salesChannels"
              placeholder={intl.formatMessage(messages.salesChannelsPlaceholder)}
              values={values.salesChannels}
              onChange={handleSalesChannelsChange}
              touched={getIn(touched, 'salesChannels')}
              errors={getIn(errors, 'salesChannels')}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <SelectTags
              id="confirmationModalities"
              label={intl.formatMessage(messages.modalitiesLabel)}
              placeholder={intl.formatMessage(messages.modalitiesPlaceholder)}
              items={getFilteredConfirmationModalities(values.confirmationModalities)}
              getTagLabel={(value: Lowercase<EConfirmationModality>): string => {
                return safeFormatMessage({ intl, key: value, messages: confirmationModalitiesMessages })
              }}
              values={values.confirmationModalities}
              onChange={(values: EConfirmationModality[]): void => {
                setFieldValue('confirmationModalities', values)
              }}
              touched={touched.confirmationModalities}
              errors={errors.confirmationModalities}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <MultiSegmentAutocomplete
              id="segments"
              label={intl.formatMessage(messages.segmentsLabel)}
              placeholder={intl.formatMessage(messages.segmentsPlaceholder)}
              values={values.segments}
              onChange={(values: GTSegmentSuggestion[]): void => {
                setFieldValue('segments', values)
              }}
              companyId={values.company.id}
              touched={touched.segments}
              errors={errors.segments}
              skip={!values.company.id}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="addonService.form.feePerTicket.label" defaultMessage="Fee per ticket" />
            </Heading.H5>
          </Column>
        </Row>
        {values.salesChannels.length > 0 ? (
          <Table>
            <Thead>
              <Tr>
                <StyledTh>
                  <FormattedMessage
                    id="addonService.form.numberOfTickets.label"
                    defaultMessage="Number of tickets on booking"
                  />
                </StyledTh>
                {currencies.map((currency) => {
                  return (
                    <StyledTh key={currency.code}>
                      <FormattedMessage
                        id="addonService.form.mxnFees.label"
                        defaultMessage="Fee per ticket (<strong>{currencyCode}</strong>)"
                        values={{
                          strong: (msg): TSFixMe => <strong>{msg}</strong>,
                          currencyCode: currency.code
                        }}
                      />
                    </StyledTh>
                  )
                })}
              </Tr>
            </Thead>
            <Tbody>
              {values.ticketFees.map(
                (ticket, tIndex): TSFixMe => (
                  <Tr key={`fee-${ticket.quantity}`}>
                    <StyledTicketCell>
                      <StyledTicketQuantity>{ticket.quantity}</StyledTicketQuantity>
                    </StyledTicketCell>
                    {ticket.fees.map(
                      (fee, fIndex): TSFixMe => (
                        <StyledCell key={`fee-${ticket.quantity}-${fee.currencyCode}`}>
                          <InputCurrency
                            id={`fee-${ticket.quantity}-${fee.currencyCode}-input`}
                            placeholder={`$0.00 ${fee.currencyCode}`}
                            suffix={` ${fee.currencyCode}`}
                            value={sanitizePrice(Number(fee.amount))}
                            onChange={(value: string): void => handleFeeChange(tIndex, fIndex, value)}
                            touched={hasFeeTouched(tIndex, fIndex, touched?.ticketFees)}
                            errors={hasFeeErrors(tIndex, fIndex, errors?.ticketFees)}
                          />
                        </StyledCell>
                      )
                    )}
                  </Tr>
                )
              )}
            </Tbody>
          </Table>
        ) : (
          <Row>
            <Column>
              <EmptyState.Container>
                <EmptyState.Description>
                  <FormattedMessage
                    id="addonService.form.ticketFees.emptyState"
                    defaultMessage="Select a sales channel to display the fees"
                  />
                </EmptyState.Description>
              </EmptyState.Container>
            </Column>
          </Row>
        )}
      </FormLayout.CardContent>
      <FormLayout.Split />
      <FormLayout.CardFooter>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="addonService.form.error"
                defaultMessage="There was an error while creating your addon service"
              />
            </ErrorMessage>
          </div>
        )}
        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/ancillaries/services" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

const NewAddonService = ({ addonService, onSubmit, error, loading, isEdit }: INewAddonServiceProps): ReactElement => {
  const intl = useIntl()

  return (
    <Formik initialValues={addonService} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(props): ReactElement => (
        <NewAddonServiceForm {...props} isEdit={isEdit} error={error} loading={loading} addonService={addonService} />
      )}
    </Formik>
  )
}

export default NewAddonService
